import { useState } from "react";
import { InfiniteListBase, usePermissions, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { NotesIterator } from "./NotesIterator";
import { NoteCreate } from "./NoteCreate";

type NoteResource = "contact_notes" | "company_notes" | "deal_notes" | "load_notes";
type Reference = "contacts" | "companies" | "deals" | "loads";

interface NotesPanelProps {
  resource: NoteResource;
  reference: Reference;
  filter: Record<string, unknown>;
  showStatus?: boolean;
  empty?: React.ReactNode;
  title?: string;
  className?: string;
}

export const NotesPanel = ({
  resource,
  reference,
  filter,
  showStatus,
  empty,
  title,
  className,
}: NotesPanelProps) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const isAdmin = permissions === "admin";
  const [showDeleted, setShowDeleted] = useState(false);

  const defaultEmpty = (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <p className="text-muted-foreground mb-4">
        {translate("resources.notes.empty")}
      </p>
      <NoteCreate reference={reference} showStatus={showStatus} />
    </div>
  );

  const content = (
    <>
      {(title || isAdmin) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h2 className="text-base font-semibold">{title}</h2>}
          {!title && <div />}
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`show-deleted-${resource}`}
                checked={showDeleted}
                onCheckedChange={(checked) => setShowDeleted(checked === true)}
              />
              <Label
                htmlFor={`show-deleted-${resource}`}
                className="text-sm font-normal"
              >
                {translate("resources.notes.show_deleted")}
              </Label>
            </div>
          )}
        </div>
      )}
      {(title || isAdmin) && <Separator className="mb-4" />}
      <InfiniteListBase
        resource={resource}
        filter={{
          ...filter,
          ...(showDeleted ? {} : { "deleted@eq": false }),
        }}
        sort={{ field: "date", order: "DESC" }}
        perPage={25}
        disableSyncWithLocation
        storeKey={false}
        empty={empty || defaultEmpty}
      >
        <NotesIterator reference={reference} showStatus={showStatus} />
      </InfiniteListBase>
    </>
  );

  if (className) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Card>
      <CardContent className="pt-4">{content}</CardContent>
    </Card>
  );
};
